import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { PortalConfigContext } from 'src/context';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { selectCurrentUser } from 'src/store/storeUtils';

export const useCompanySwitcherVisibility = (): {
  visibility: 'mobile' | 'desktop' | 'hidden';
} => {
  // Initialize state with cached value if available to prevent layout shift
  const [hasMultipleCompanies, setHasMultipleCompanies] = React.useState(() => {
    // Try to get the cached value synchronously on initial render
    // This prevents the drawer width from being incorrect during navigation
    const cachedValue = localStorage.getItem(
      'companySwitcher.hasMultipleCompanies',
    );
    return cachedValue === 'true';
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const portalConfig = React.useContext(PortalConfigContext);
  const companiesEnabled = !portalConfig.features.disableCompanies;
  const currentUser = useAppSelector(selectCurrentUser);
  const isClient = useAppSelector((state) => state.user.isClient);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (currentUser && isClient) {
      const hasMultipleCompaniesValue =
        (currentUser?.fields.companyIds?.length ?? 0) > 1;
      // Update localStorage for persistent storage
      localStorage.setItem(
        'companySwitcher.hasMultipleCompanies',
        String(hasMultipleCompaniesValue),
      );
      setHasMultipleCompanies(hasMultipleCompaniesValue);

      // the switcher is responsible for setting the active company based
      // on the user's selection or default to the first company if no selection.
      // when user does not have multiple companies the switcher will not show
      // so we should default to setting to activeCompanyId the single company
      // the user is a member of.
      if (!hasMultipleCompaniesValue) {
        const companyId = currentUser?.fields.companyIds?.at(0);
        if (companyId) {
          dispatch(setActiveCompanyId(companyId));
        }
      }
    }
  }, [currentUser, dispatch, isClient]);

  const isVisible = hasMultipleCompanies && isClient && companiesEnabled;
  if (isVisible) {
    return {
      visibility: isMobile ? 'mobile' : 'desktop',
    };
  }

  return {
    visibility: 'hidden',
  };
};
